import isUndefined from 'lodash-es/isUndefined';
import { fromEvent, merge, Observable } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { UndoService } from '../..';
import { isNode } from '../../helpers/dom/node';
import { findImageInRange, isImageElement } from '../../helpers/image';
import { createConnectingService } from '../base/connectingService';
import { ElementSelectionService } from '../dom/elementSelectionService';
import { ElementService } from '../dom/elementService';
import { ImageService, ImageWithUrlOptions, NULL_IMAGE_SERVICE } from '../rich/imageService';
import { createHtmlImageResizeEffect } from './htmlImageResizeEffect';

export interface HtmlImageServiceOptions {
  isImageInteractive?: (image: HTMLImageElement) => boolean;
}

export function createHtmlImageService(
  element$: Observable<Element | null>,
  selectionService: ElementSelectionService,
  undoService: UndoService,
  options: HtmlImageServiceOptions = {},
): ImageService {
  return createConnectingService(
    element$,
    () => NULL_IMAGE_SERVICE,
    element => new HtmlImageService(element, selectionService, undoService, options),
  );
}

export class HtmlImageService extends ElementService<Element> implements ImageService {
  constructor(
    element: Element,
    selectionService: ElementSelectionService,
    undoService: UndoService,
    options: HtmlImageServiceOptions = {},
  ) {
    super(element, selectionService, {
      effects$: merge(
        // Select images on click.
        fromEvent<MouseEvent>(element, 'click').pipe(
          tap(event => {
            if (
              isNode(event.target) &&
              isImageElement(event.target) &&
              this.isImageInteractive(event.target)
            ) {
              event.preventDefault();

              // TODO: Show selection indication in Firefox.
              const range = this.document.createRange();
              range.selectNode(event.target);
              this.selectionService.selectWithRange(range, false);
            }
          }),
          share(),
        ),
        createHtmlImageResizeEffect(element, image => this.isImageInteractive(image), undoService),
      ),
    });

    this.selectionService = selectionService;
    this.isImageInteractive = options.isImageInteractive || (() => true);
  }

  private readonly selectionService: ElementSelectionService;
  private readonly isImageInteractive: (image: HTMLImageElement) => boolean;

  get image(): HTMLImageElement | null {
    const { selection } = this.selectionService;
    if (!selection) {
      return null;
    }

    const { element } = this;
    const { range } = selection;
    const image = findImageInRange(element, range);
    return image && this.isImageInteractive(image) ? image : null;
  }

  get url() {
    const { image } = this;
    return image ? image.getAttribute('src') || '' : undefined;
  }

  insertImageWithUrl(url: string, { initialHeight, maxHeight }: ImageWithUrlOptions = {}) {
    const { selection } = this.selectionService;
    if (!selection) {
      return;
    }

    const { document } = this;
    const image = document.createElement('img');
    image.setAttribute('src', url);

    if (maxHeight) {
      image.setAttribute('data-editor-max-height', String(maxHeight));
    }

    if (initialHeight) {
      /**
       * @see HtmlImageResizeController#constructor
       * At this time, we don't have the image.naturalWidth & image.naturalHeight that HtmlImageResizeController uses
       * uses to determine the aspect ratio because the image src hasn't been downloaded yet.
       */
      image.style.width = 'auto';
      image.style.height = `${initialHeight}px`;
    }

    const { range } = selection;
    range.deleteContents();

    range.insertNode(image);

    // It is expected that the image has a parentNode at this time after getting inserted into the range.
    if (image.parentNode) {
      // To place the cursor after the image, we insert an empty text node after the image & then move the cursor to it.
      const emptyText = document.createTextNode('');
      image.parentNode.insertBefore(emptyText, image.nextSibling);
      this.selectionService.moveToOffsetInContainer(emptyText, 0);
    }
  }

  setUrl(url: string | undefined) {
    const { image } = this;

    if (!image) {
      if (!isUndefined(url)) {
        this.insertImageWithUrl(url);
      }

      return;
    }

    if (!isUndefined(url)) {
      image.setAttribute('src', url);
      return;
    }

    const { parentNode } = image;
    if (parentNode) {
      parentNode.removeChild(image);
    }
  }
}
